<script setup lang="ts">
  import { ref, onMounted, watch, onBeforeUnmount, nextTick, useTemplateRef, useId, computed } from 'vue';
  import type { WatchStopHandle } from '@vue/runtime-core';
  import { useFormContext, getModelFromContext, setModelWithContext } from '@othercommerce/foundation';

  interface Props {
    id?: string | undefined;
    name?: string | undefined;
    label?: string | undefined;
    placeholder?: string | undefined;
    floating?: boolean;
    required?: boolean;
    readonly?: boolean;
  }

  defineOptions({
    inheritAttrs: false,
  });

  const props = withDefaults(defineProps<Props>(), {
    floating: true,
    required: false,
    readonly: false,
  });

  const htmlControl = useTemplateRef('htmlControl');
  const context = useFormContext();
  const random = useId();

  const model = defineModel({
    get: (value) => getModelFromContext(props.name, context, value),
    set: (value) => setModelWithContext(props.name, context, value),
  });

  const id = computed(() => {
    return props.id ?? random;
  });

  const readonly = computed(() => {
    return Boolean(props.readonly || (context && context.readonly.value));
  });

  function resize() {
    htmlControl.value!.style.height = '0px';

    let style = window.getComputedStyle(htmlControl.value!);
    let top = Number(style.getPropertyValue('border-top-width').replace('px', ''));
    let bottom = Number(style.getPropertyValue('border-bottom-width').replace('px', ''));
    let height = htmlControl.value!.scrollHeight + top + bottom;

    htmlControl.value!.style.height = height + 'px';
  }

  const watchStopHandle = ref<WatchStopHandle>();

  onMounted(() => {
    resize();

    watchStopHandle.value = watch(model, () => {
      nextTick(() => {
        resize();
      });
    });
  });

  onBeforeUnmount(() => {
    if (watchStopHandle.value) {
      watchStopHandle.value();
    }
  });
</script>
<template>
  <SfControlWrapper v-bind="{ id, name, label, required, floating }" v-slot="{errorId}">
    <textarea
      ref="htmlControl"
      class="control"
      :placeholder="placeholder || label"
      v-bind="{ id, name, required, readonly, ...$attrs }"
      v-model="model"
      :aria-describedby="errorId"
    />
  </SfControlWrapper>
</template>
<style scoped lang="scss">
  .control {
    resize: none;
    min-height: var(--sf-textarea-min-height);
    //max-height: var(--sf-textarea-max-height);
  }
</style>
